import React, { useState } from 'react';
import {
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';

/**
 * ShiftPanel Component
 * Manages shift start/end UI.
 * onEndShiftClick — called when cashier clicks End Shift; parent mounts CloseShiftModal.
 */
export const ShiftPanel = ({
  isShiftActive,
  shift,
  onStartShift,
  onEndShiftClick,
  loading,
  error,
  userName
}) => {
  const [startingCash, setStartingCash] = useState('0');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  const handleStartShift = async () => {
    // Validate inputs
    if (!employeeId.trim()) {
      setPinError('Employee ID is required');
      return;
    }
    
    const trimmedPin = pin.trim();
    if (!trimmedPin) {
      setPinError('PIN is required');
      return;
    }
    
    
    console.log('Shift panel trimmedPin', trimmedPin);
    if (trimmedPin.length < 4) {
      setPinError('PIN must be exactly 4 digits');
      return;
    }
    
    try {
      setPinError('');
      // Pass correct parameters: cashierId, pin, startingDenominations
      const startingDenominations = {
        "1000": 0,
        "500": 0,
        "200": 0,
        "100": 0,
        "50": 0,
        "20": 0,
        "10": 0,
        "5": 0,
        "1": 0
      };
      await onStartShift(employeeId.trim(), trimmedPin, startingDenominations);
      // Reset form on success
      setStartingCash('0');
      setEmployeeId('');
      setPin('');
      setShowStartDialog(false);
    } catch (err) {
      console.error('Start shift error:', err);
      setPinError(err.message || 'Failed to start shift');
    }
  };


  if (!isShiftActive) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-red-900">No Active Shift</h3>
            <p className="text-sm text-red-700 mt-1">
              {userName ? `${userName}, start your shift to begin taking orders.` : 'Start your shift to begin taking orders.'}
            </p>
          </div>
          <button
            onClick={() => setShowStartDialog(true)}
            disabled={loading}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 transition"
          >
            <PlayIcon className="h-5 w-5" />
            <span>Start Shift</span>
          </button>
        </div>

        {/* Start Shift Dialog */}
        {showStartDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-w-full mx-4">
              <h3 className="text-lg font-bold mb-4">Start Shift</h3>
              <p className="text-gray-600 mb-4">Enter your credentials and starting cash</p>
              
              {/* Employee ID */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee ID
                </label>
                <input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="Enter your Employee ID"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {/* PIN */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PIN
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter your 4-digit PIN"
                  maxLength="6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Starting Cash */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Starting Cash (Optional)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={startingCash}
                  onChange={(e) => setStartingCash(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {(error || pinError) && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
                  {pinError || error}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowStartDialog(false);
                    setPinError('');
                    setEmployeeId('');
                    setPin('');
                    setStartingCash('0');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartShift}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Starting...' : 'Start Shift'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Active shift display
  const shiftDuration = shift?.startTime 
    ? Math.floor((new Date() - new Date(shift.startTime)) / 1000 / 60)
    : 0;
  const hours = Math.floor(shiftDuration / 60);
  const minutes = shiftDuration % 60;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-green-900">Shift Active</h3>
          <p className="text-sm text-green-700 mt-1">
            Duration: {hours}h {minutes}m
          </p>
          {shift?.startingBalance !== undefined && (
            <p className="text-sm text-green-700">
              Starting Cash: ₱{shift.startingBalance.toFixed(2)}
            </p>
          )}
        </div>
        <button
          onClick={onEndShiftClick}
          disabled={loading}
          className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 transition"
        >
          <StopIcon className="h-5 w-5" />
          <span>End Shift</span>
        </button>
      </div>
    </div>
  );
};

export default ShiftPanel;
